import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { RequestPriority, RequestStatus } from "../types"

// Define the form schema with strict typing
const formSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  department: z.string().min(2, "Department is required"),
  position: z.string().min(2, "Position is required"),
  numberOfPositions: z.coerce.number().int().positive("Must be a positive number"),
  description: z.string().optional(),
  projectCode: z.string().optional(),
  status: z.nativeEnum(RequestStatus).default(RequestStatus.DRAFT),
  priority: z.nativeEnum(RequestPriority).default(RequestPriority.MEDIUM),
})

// Define the type from our schema
type BasicInfoFormValues = z.infer<typeof formSchema>

interface BasicInfoFormProps {
  formData: Partial<BasicInfoFormValues>;
  updateFormData: (data: BasicInfoFormValues) => void;
  onNext: () => void;
  isFirstStep: boolean;
}

export function BasicInfoForm({ formData, updateFormData, onNext, isFirstStep }: BasicInfoFormProps) {
  const form = useForm<BasicInfoFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: formData.title || "",
      department: formData.department || "",
      position: formData.position || "",
      numberOfPositions: formData.numberOfPositions || 1,
      description: formData.description || "",
      projectCode: formData.projectCode || "",
      status: formData.status || RequestStatus.DRAFT,
      priority: formData.priority || RequestPriority.MEDIUM,
    },
  })

  const onSubmit = (data: BasicInfoFormValues) => {
    updateFormData(data)
    onNext()
  }

  // Sample departments - in a real app, these would come from an API
  const departments = [
    "Engineering",
    "Product",
    "Design",
    "Marketing",
    "Sales",
    "Customer Support",
    "Human Resources",
    "Finance",
    "Legal",
    "Operations",
  ]

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Request Title</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Senior Frontend Developer" {...field} />
              </FormControl>
              <FormDescription>A clear title for the resource request</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="department"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Department</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>The department requesting this resource</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Position</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Software Engineer" {...field} />
              </FormControl>
              <FormDescription>The job title or position being requested</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="numberOfPositions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Number of Positions</FormLabel>
              <FormControl>
                <Input type="number" min="1" {...field} />
              </FormControl>
              <FormDescription>How many people are needed for this role</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="projectCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Code</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., PRJ-2023-001" {...field} />
                </FormControl>
                <FormDescription>Optional project identifier code</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={RequestPriority.LOW}>Low</SelectItem>
                    <SelectItem value={RequestPriority.MEDIUM}>Medium</SelectItem>
                    <SelectItem value={RequestPriority.HIGH}>High</SelectItem>
                    <SelectItem value={RequestPriority.URGENT}>Urgent</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>Urgency level of this request</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Provide a detailed description of the role and responsibilities"
                  className="min-h-32"
                  {...field}
                />
              </FormControl>
              <FormDescription>A comprehensive description of the role and its responsibilities</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="submit">Next Step</Button>
        </div>
      </form>
    </Form>
  )
}

